import { WindowManager } from './WindowManager';
import { EventListener } from 'src/models/eventManager/EventListener';
import { EventSource } from 'src/models/eventManager/EventSource';
import { EventSubscription } from 'src/models/eventManager/EventSubscription';
import { EventTypeDetail } from 'src/models/eventManager/EventTypeDetail';
import { EventTypes } from 'src/models/eventManager/EventTypes';
import { SourceEventManager } from './SourceEventManager';

export class EventManager {
	static key = 'EventManager';

	public static init() {
		if (!WindowManager.has(EventManager.key)) {
			WindowManager.set(EventManager.key, new EventManager());
		}
	}

	public static destroy() {
		const manager: EventManager = WindowManager.get(EventManager.key);
		if (manager) {
			manager.sources.clear();
			WindowManager.set(EventManager.key, undefined);
		}
	}

	public static get(): EventManager | undefined {
		if (!WindowManager.has(EventManager.key)) {
			console.error(`${EventManager.key} instance not found!`)
			return;
		}
		return WindowManager.get(EventManager.key);
	}

    sources: Map<string, SourceEventManager> = new Map();

	public addSource(sourceKey: string) {
		const sourceManager = new SourceEventManager(sourceKey);
		if (!this.sources.has(sourceManager.key)) {
			this.sources.set(sourceManager.key, sourceManager);
		}
	}

	public subscribe<TType extends EventTypes, TDetail extends EventTypeDetail>(sourceKey: string, subscription: EventSubscription<TType, TDetail>): void {
		let sourceManager = this.getSource(sourceKey);
		// create if the publisher hasn't created it yet, it is safe to assume the publish will simply re-use this source when it has initialised.
		if (!sourceManager) {
			this.addSource(sourceKey);
			sourceManager = this.getSource(sourceKey);
		}
		sourceManager.subscribe(subscription);
	}

	public unsubscribe<TType extends EventTypes, TDetail extends EventTypeDetail>(sourceKey: string, type: TType, listener: EventListener): void {
		const sourceManager = this.getSource(sourceKey);
		// if the source doesn't exist, it is not needed to remove the listener as it doesn't exist.
		if (sourceManager) {
			sourceManager.unsubscribe<TType, TDetail>(type, listener);
		}
	}

	public publish<TDetail extends EventTypeDetail, TEventSource extends EventSource>(sourceKey: string, source: TEventSource, detail: TDetail) {
		const sourceManager = this.getSource(sourceKey);
		if (sourceManager && sourceManager.getSubscribers(detail.type).length > 0) {
			sourceManager.publish(source, detail);
		}

		const workingSourceKey = SourceEventManager.generateKey(source, true);
		const sourceManagerWithoutAppId = this.getSource(workingSourceKey);
		if (sourceManagerWithoutAppId && sourceManagerWithoutAppId.getSubscribers(detail.type).length > 0){
			sourceManagerWithoutAppId.publish(source, detail);
		}

		const workingSourceKeyWithAppIdOnly = source.applicationId;
		const sourceManagerWithAppIdOnly = this.getSource(workingSourceKeyWithAppIdOnly);
		if (sourceManagerWithAppIdOnly && sourceManagerWithAppIdOnly.getSubscribers(detail.type).length > 0){
			sourceManagerWithAppIdOnly.publish(source, detail);
		}
	}

    private getSource(sourceKey: string) {
		return this.sources.get(sourceKey);
	}
}


// WEBPACK FOOTER //
// ./src/utils/EventManager.ts